'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'

import { GetProductInfoResponse } from '../../../shared/api/generated'
import { useProductsArchiveProductMutation } from '../../../entities/marketItem/queries'
import { parseStorageJSON } from '../../../shared/storage/browserStorage'
import { FlexColumn, FlexRowBetween, StyledButton } from '../../../shared/ui-kit/projectComponents'
import { MiniModal } from '../../../shared/ui-kit/actual/modals/MiniModal'
import { H4 } from '../../../shared/ui-kit/titles'
import { AppProvider } from '../../providers/AppProvider/AppProvider'
import Order from '../../../features/marketItem/ui/order'

interface ItemOrderClientProps {
  ownerId?: string
  productInfo: GetProductInfoResponse
}

type StoredUser = { UserId?: string }

export function ItemOrderClient({ ownerId, productInfo }: ItemOrderClientProps) {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const [isOwner, setIsOwner] = useState<boolean | null>(null)

  useEffect(() => {
    const updateOwner = () => {
      const user = parseStorageJSON<StoredUser | null>(localStorage.getItem('user'), null)
      const isAuthenticated = parseStorageJSON<boolean>(localStorage.getItem('isAuth'), false)
      const accessToken = parseStorageJSON<string | null>(localStorage.getItem('accessToken'), null)
      const productOwnerId = ownerId || productInfo.owner?.id
      setIsOwner(
        Boolean(isAuthenticated && accessToken && productOwnerId && user?.UserId === productOwnerId)
      )
    }
    updateOwner()
    window.addEventListener('storage', updateOwner)
    return () => window.removeEventListener('storage', updateOwner)
  }, [ownerId, productInfo.owner?.id])

  const handleOpen = () => {
    const accessToken = parseStorageJSON<string | null>(localStorage.getItem('accessToken'), null)
    const isAuthenticated = parseStorageJSON<boolean>(localStorage.getItem('isAuth'), false)

    if (!accessToken || !isAuthenticated) {
      router.push('/login')
      return
    }

    const user = parseStorageJSON<StoredUser | null>(localStorage.getItem('user'), null)
    if (user?.UserId && user.UserId === (ownerId || productInfo.owner?.id)) return

    setIsOpen(true)
  }

  if (isOwner === null) return null

  if (isOwner) {
    return (
      <AppProvider>
        <OwnerProductActions productInfo={productInfo} />
      </AppProvider>
    )
  }

  return (
    <>
      <StyledButton type="button" onClick={handleOpen} variant={'primary'} fullwidth>
        Добавить в корзину
      </StyledButton>

      {isOpen && (
        <AppProvider>
          <Order openMenu productInfo={productInfo} setViewModal={setIsOpen} />
        </AppProvider>
      )}
    </>
  )
}

function OwnerProductActions({ productInfo }: { productInfo: GetProductInfoResponse }) {
  const router = useRouter()
  const [confirmArchive, setConfirmArchive] = useState(false)
  const archive = useProductsArchiveProductMutation()

  const closeConfirmation = (open: boolean) => {
    if (!archive.isLoading) setConfirmArchive(open)
  }
  const handleArchive = async () => {
    if (archive.isLoading || !productInfo.id) return
    try {
      await archive.mutateAsync({ productId: productInfo.id })
      setConfirmArchive(false)
      router.replace('/profile')
      router.refresh()
    } catch {
      // The mutation displays the backend error through a toast.
    }
  }

  if (productInfo.status && productInfo.status !== 'active') return null

  return (
    <>
      <StyledButton
        type="button"
        onClick={() => setConfirmArchive(true)}
        variant="warning"
        fullwidth
      >
        Снять объявление
      </StyledButton>
      <MiniModal viewModal={confirmArchive} setViewModal={closeConfirmation} minWidth>
        <FlexColumn style={{ gap: 24 }}>
          <H4>Снять объявление «{productInfo.name}»? Оно будет перемещено в архив.</H4>
          <FlexRowBetween style={{ gap: 16, width: '100%' }}>
            <StyledButton
              type="button"
              variant="secondary"
              disabled={archive.isLoading}
              onClick={() => closeConfirmation(false)}
              fullwidth
            >
              Отмена
            </StyledButton>
            <StyledButton
              type="button"
              variant="warning"
              disabled={archive.isLoading}
              onClick={handleArchive}
              fullwidth
            >
              {archive.isLoading ? 'Снимаем…' : 'Снять объявление'}
            </StyledButton>
          </FlexRowBetween>
        </FlexColumn>
      </MiniModal>
    </>
  )
}
